import { Eye, LogOut, ShieldAlert } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { formatCountdown, formatTimeIst } from '@/lib/format';
import { EASE_OUT } from '@/lib/motion';
import { useNow } from '@/lib/use-now';

/** Persistent strip shown for the whole of a read-only Super Admin support session. */
export function SupportBanner({
  tenantName,
  expiresAt,
  onEnd,
}: {
  tenantName: string;
  expiresAt: string;
  onEnd: () => void;
}) {
  const now = useNow(1000);
  const remaining = new Date(expiresAt).getTime() - now;
  return (
    <motion.section
      aria-label="Support session"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
      className="relative z-40 shrink-0 overflow-hidden border-b border-warning/30 bg-warning-soft"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(135deg,transparent_0_14px,color-mix(in_oklab,var(--warning)_7%,transparent)_14px_28px)]"
        aria-hidden
      />
      <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:px-6">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning ring-1 ring-warning/30">
          <ShieldAlert className="size-4" aria-hidden />
        </span>
        <p className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-fg">
          <span className="font-semibold">Support session</span>
          <span className="text-fg-subtle" aria-hidden>
            ·
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-warning">
            <Eye className="size-3.5" aria-hidden /> read-only
          </span>
          <span className="text-fg-subtle" aria-hidden>
            ·
          </span>
          <span className="text-fg-muted">
            viewing <span className="font-medium text-fg">{tenantName}</span>
          </span>
          <span className="text-fg-subtle" aria-hidden>
            ·
          </span>
          <span className="text-fg-muted">
            ends <time dateTime={expiresAt}>{formatTimeIst(expiresAt)}</time>
            <span className="tabular ml-1.5 rounded-full bg-surface/70 px-1.5 py-px text-xs text-fg-muted ring-1 ring-warning/20">
              {formatCountdown(remaining)} left
            </span>
          </span>
        </p>
        <Button size="sm" variant="secondary" onClick={onEnd} className="border-warning/40">
          <LogOut aria-hidden /> End session
        </Button>
      </div>
    </motion.section>
  );
}
